import { useCallback, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import TableSimple from "ds/components/TableSimple";
import Box from "ds/components/Box";
import TooltipInfo from "ds/components/TooltipInfo";
import PageInfoHeaderTitle from "ds/components/PageInfoHeader/Title";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import Button from "ds/components/Button";
import useTableSort from "components/Table/useTableSort";
import { SearchQueryOrderDirection } from "types/generated";
import useBulkActionsFloatingBarHeight from "components/BulkActions/FloatingBar/hooks/useBulkActionsFloatingBarHeight";
import { selectSelectionIsStarted } from "ds/components/Table/store/useTableSelectionStore/selectors";
import useShiftSelectionManager from "components/BulkActions/useShiftSelectionManager";
import useFlowsProject from "shared/Flows/Projects/useFlowsProject";

import FLOWS_PROJECT_ROLES_CELLS from "./Cells";
import {
  FLOWS_PROJECT_ROLES_COLUMN_ID,
  FLOWS_PROJECT_ROLES_FILTERS_STORAGE_KEY,
  FLOWS_PROJECT_ROLES_SORT_CONFIG,
} from "./constants";
import FlowsProjectDetailsRolesEmpty from "./Empty";
import { useFilteredFlowsProjectRoles } from "./helpers";
import { showFlowsProjectDetailsRolesManageRolesDrawer } from "./ManageRolesDrawer";
import FlowsProjectDetailsRolesBulkActions from "./BulkActions";
import useFlowsProjectRolesTableStore from "./useStore";
import useFlowsProjectRolesTableSelectionStore from "./useSelectionStore";
import useFlowsProjectRolesSelectionOptions from "./useSelectionOptions";

type FlowsProjectDetailsRolesProps = {
  projectId: string;
};

const FlowsProjectDetailsRoles = ({ projectId }: FlowsProjectDetailsRolesProps) => {
  const { roleBindings, loading, hasData, refetch } = useFlowsProject({ projectId });

  const loadingContent = loading && !hasData;

  const { sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: FLOWS_PROJECT_ROLES_FILTERS_STORAGE_KEY,
    sortConfig: FLOWS_PROJECT_ROLES_SORT_CONFIG,
    initialSortOption: FLOWS_PROJECT_ROLES_SORT_CONFIG[FLOWS_PROJECT_ROLES_COLUMN_ID.ROLE],
    initialSortDirection: SearchQueryOrderDirection.Asc,
  });

  const sortedRoles = useFilteredFlowsProjectRoles(roleBindings, sortDescriptor);

  const { selectionOptions, rolesMap } = useFlowsProjectRolesSelectionOptions(sortedRoles);

  const { isBulkActionsActive, toggle, clearSelected, selectedSet, setAllItemsCount } =
    useFlowsProjectRolesTableSelectionStore(
      useShallow((state) => ({
        isBulkActionsActive: selectSelectionIsStarted(state),
        toggle: state.toggle,
        clearSelected: state.clearSelected,
        selectedSet: state.selected,
        replaceSelected: state.replaceSelected,
        setAllItemsCount: state.setAllItemsCount,
      }))
    );

  useEffect(() => {
    setAllItemsCount(sortedRoles.length);
  }, [setAllItemsCount, sortedRoles.length]);

  useEffect(() => {
    return () => {
      clearSelected();
    };
  }, [clearSelected]);

  const { height: floatingBarHeight, setHeight: setFloatingBarHeight } =
    useBulkActionsFloatingBarHeight(isBulkActionsActive);

  const shiftSelectionAction = useShiftSelectionManager({
    collection: sortedRoles,
    selectedIds: selectedSet,
  });

  const handleManageRoles = () => {
    showFlowsProjectDetailsRolesManageRolesDrawer({ projectId });
  };

  const onBulkActionsFinish = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const hasRoles = sortedRoles.length > 0;

  return (
    <Box direction="column" grow="1">
      <PageInfoHeaderWrapper>
        <Box align="center" gap="medium">
          <PageInfoHeaderTitle title="Roles" />
          <TooltipInfo widthMode="maxWidthSm">
            Roles grant this project permission to manage resources in other spaces.
          </TooltipInfo>
        </Box>

        {hasRoles && (
          <Button variant="primary" onPress={handleManageRoles}>
            Manage roles
          </Button>
        )}
      </PageInfoHeaderWrapper>

      {(hasRoles || loadingContent) && (
        <TableSimple
          data={sortedRoles}
          loading={loadingContent}
          store={useFlowsProjectRolesTableStore}
          selectionStore={useFlowsProjectRolesTableSelectionStore}
          selectionOptions={selectionOptions}
          shiftSelectionAction={shiftSelectionAction}
          ariaLabel="Flows project roles table"
          cellPerColumn={FLOWS_PROJECT_ROLES_CELLS}
          filtersStorageKey={FLOWS_PROJECT_ROLES_FILTERS_STORAGE_KEY}
          sortConfig={FLOWS_PROJECT_ROLES_SORT_CONFIG}
          hasPagination={false}
          bottomOffset={floatingBarHeight}
        />
      )}

      {!hasRoles && !loadingContent && (
        <FlowsProjectDetailsRolesEmpty onManageRoles={handleManageRoles} />
      )}

      <Box fullWidth relative>
        <FlowsProjectDetailsRolesBulkActions
          selectedSet={selectedSet}
          rolesMap={rolesMap}
          onBulkResetAll={clearSelected}
          onItemDismiss={toggle}
          onFinish={onBulkActionsFinish}
          onHeightChange={setFloatingBarHeight}
        />
      </Box>
    </Box>
  );
};

export default FlowsProjectDetailsRoles;
